import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { popupConfirm } from 'utils/popup';
import type { TFunction } from 'i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

export function isScheduledDateTimeInPast(
  status: number,
  date: string | Dayjs | null | undefined,
  time: string | null | undefined,
  pageTimezone?: string | null,
): boolean {
  if (status !== STATUS.SCHEDULED || !date || !time) {
    return false;
  }

  const day = dayjs.isDayjs(date) ? date.format('YYYY-MM-DD') : date;
  const zone = pageTimezone ?? dayjs.tz.guess();
  const scheduledAt = dayjs.tz(`${day} ${time}`, 'YYYY-MM-DD HH:mm', zone);

  return scheduledAt.isValid() && scheduledAt.isBefore(dayjs());
}

type ScheduledDateTime = {
  status: number;
  date: string | Dayjs | null | undefined;
  time: string | null | undefined;
  pageTimezone?: string | null;
};

/**
 * Guards moving several posts at once (e.g. a calendar drag & drop). Asks once for the whole
 * batch and returns false when the user declined, in which case the caller must not save any
 * of them.
 */
export async function canSaveScheduledDateTimes(
  posts: ScheduledDateTime[],
  t: TFunction,
): Promise<boolean> {
  const movedIntoPast = posts.filter(({ status, date, time, pageTimezone }) =>
    isScheduledDateTimeInPast(status, date, time, pageTimezone),
  );

  if (movedIntoPast.length === 0) {
    return true;
  }

  return confirmPublishInPast(t, movedIntoPast.length);
}

/**
 * Guards a save that could move a scheduled post into the past. Returns false when the user
 * declined the "publish immediately" confirmation, in which case the caller must not save.
 */
export async function canSaveScheduledDateTime(
  post: ScheduledDateTime,
  t: TFunction,
): Promise<boolean> {
  return canSaveScheduledDateTimes([post], t);
}

export async function confirmPublishInPast(
  t: TFunction,
  count = 1,
): Promise<boolean> {
  const result = await popupConfirm({
    title: t('scheduledPastDateTitle', {
      defaultValue: 'Publish immediately?',
    }),
    text:
      count > 1
        ? t('scheduledPastDateWarning_plural', {
            count,
            defaultValue:
              '{{count}} scheduled posts would move to a date and time in the past. Saving will publish them immediately. Do you want to continue?',
          })
        : t('scheduledPastDateWarning', {
            defaultValue:
              'The selected date and time are in the past. Saving will publish this post immediately. Do you want to continue?',
          }),
    confirmButtonText: t('accept', { defaultValue: 'Accept' }),
    customClass: { popup: '!tw-w-[400px]' },
  });

  return result.isConfirmed;
}
